import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Chip, ChipColor } from '@app/core';
import { Theme } from '@app/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  pulse: {
    animation: '$pulse 1s ease-in-out',
    transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
  },

  '@keyframes pulse': {
    from: {
      transform: 'scale(1)',
      boxShadow: 'none',
    },
    '50%': {
      transform: 'scale(1.3)',
      boxShadow: theme.boxShadow.base,
    },
    to: {
      transform: 'scale(1)',
      boxShadow: 'none',
    },
  },
}));

interface Props {
  value: number;
  color: ChipColor;
}

export const AnimatedChip = ({ value, color }: Props) => {
  const classes = useStyles();
  const prevValueRef = useRef(value);
  const [pulse, setPulse] = useState(false);
  const [variant, setVariant] = useState('default');

  useEffect(() => {
    const prev = prevValueRef.current;
    if (prev === value) return;
    if (value > prev) {
      setPulse(true);
      setVariant('inverted');
      const timeoutId = setTimeout(() => {
        setPulse(false);
        setVariant('default');
      }, 1000);

      prevValueRef.current = value;

      return () => clearTimeout(timeoutId);
    }
    prevValueRef.current = value;
    return;
  }, [value]);

  return (
    <Chip
      color={color}
      className={classnames({
        [classes.pulse]: pulse,
      })}
      variant={variant as 'default' | 'inverted'}
    >
      {value}
    </Chip>
  );
};
